"use client";

import { availableThemes, type ThemeConfig } from "@/styles/theme-config";
import type React from "react";
import { createContext, useContext, useEffect, useState } from "react";

type Theme = "light" | "dark";

type ThemeColors = {
	background: string;
	foreground: string;
	card: string;
	cardForeground: string;
	popover: string;
	popoverForeground: string;
	primary: string;
	primaryForeground: string;
	secondary: string;
	secondaryForeground: string;
	muted: string;
	mutedForeground: string;
	accent: string;
	accentForeground: string;
	destructive: string;
	destructiveForeground: string;
	border: string;
	input: string;
	ring: string;
	chart1: string;
	chart2: string;
	chart3: string;
	chart4: string;
	chart5: string;
	sidebar: string;
	sidebarForeground: string;
	sidebarPrimary: string;
	sidebarPrimaryForeground: string;
	sidebarAccent: string;
	sidebarAccentForeground: string;
	sidebarBorder: string;
	sidebarRing: string;
};

type ThemeContextType = {
	theme: Theme;
	colorTheme: string;
	colors: ThemeColors;
	toggleTheme: () => void;
	setColorTheme: (theme: string) => void;
	availableColorThemes: ThemeConfig[];
};

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

const getThemeColors = (): ThemeColors => {
	if (typeof window !== "undefined") {
		const style = getComputedStyle(document.documentElement);
		return {
			background: style.getPropertyValue("--background"),
			foreground: style.getPropertyValue("--foreground"),
			card: style.getPropertyValue("--card"),
			cardForeground: style.getPropertyValue("--card-foreground"),
			popover: style.getPropertyValue("--popover"),
			popoverForeground: style.getPropertyValue("--popover-foreground"),
			primary: style.getPropertyValue("--primary"),
			primaryForeground: style.getPropertyValue("--primary-foreground"),
			secondary: style.getPropertyValue("--secondary"),
			secondaryForeground: style.getPropertyValue("--secondary-foreground"),
			muted: style.getPropertyValue("--muted"),
			mutedForeground: style.getPropertyValue("--muted-foreground"),
			accent: style.getPropertyValue("--accent"),
			accentForeground: style.getPropertyValue("--accent-foreground"),
			destructive: style.getPropertyValue("--destructive"),
			destructiveForeground: style.getPropertyValue("--destructive-foreground"),
			border: style.getPropertyValue("--border"),
			input: style.getPropertyValue("--input"),
			ring: style.getPropertyValue("--ring"),
			chart1: style.getPropertyValue("--chart-1"),
			chart2: style.getPropertyValue("--chart-2"),
			chart3: style.getPropertyValue("--chart-3"),
			chart4: style.getPropertyValue("--chart-4"),
			chart5: style.getPropertyValue("--chart-5"),
			sidebar: style.getPropertyValue("--sidebar"),
			sidebarForeground: style.getPropertyValue("--sidebar-foreground"),
			sidebarPrimary: style.getPropertyValue("--sidebar-primary"),
			sidebarPrimaryForeground: style.getPropertyValue("--sidebar-primary-foreground"),
			sidebarAccent: style.getPropertyValue("--sidebar-accent"),
			sidebarAccentForeground: style.getPropertyValue("--sidebar-accent-foreground"),
			sidebarBorder: style.getPropertyValue("--sidebar-border"),
			sidebarRing: style.getPropertyValue("--sidebar-ring"),
		};
	}
	// Return default values for SSR
	return {
		background: "oklch(1 0 0)",
		foreground: "oklch(0.1450 0 0)",
		card: "oklch(1 0 0)",
		cardForeground: "oklch(0.1450 0 0)",
		popover: "oklch(1 0 0)",
		popoverForeground: "oklch(0.1450 0 0)",
		primary: "oklch(0.2050 0 0)",
		primaryForeground: "oklch(0.9850 0 0)",
		secondary: "oklch(0.9700 0 0)",
		secondaryForeground: "oklch(0.2050 0 0)",
		muted: "oklch(0.9700 0 0)",
		mutedForeground: "oklch(0.5560 0 0)",
		accent: "oklch(0.9700 0 0)",
		accentForeground: "oklch(0.2050 0 0)",
		destructive: "oklch(0.5770 0.2450 27.3250)",
		destructiveForeground: "oklch(1 0 0)",
		border: "oklch(0.9220 0 0)",
		input: "oklch(0.9220 0 0)",
		ring: "oklch(0.7080 0 0)",
		chart1: "oklch(0.8100 0.1000 252)",
		chart2: "oklch(0.6200 0.1900 260)",
		chart3: "oklch(0.5500 0.2200 263)",
		chart4: "oklch(0.4900 0.2200 264)",
		chart5: "oklch(0.4200 0.1800 266)",
		sidebar: "oklch(0.9850 0 0)",
		sidebarForeground: "oklch(0.1450 0 0)",
		sidebarPrimary: "oklch(0.2050 0 0)",
		sidebarPrimaryForeground: "oklch(0.9850 0 0)",
		sidebarAccent: "oklch(0.9700 0 0)",
		sidebarAccentForeground: "oklch(0.2050 0 0)",
		sidebarBorder: "oklch(0.9220 0 0)",
		sidebarRing: "oklch(0.7080 0 0)",
	};
};

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
	const [theme, setTheme] = useState<Theme>(() => {
		if (typeof window !== "undefined") {
			const savedTheme = localStorage.getItem("theme") as Theme | null;
			const prefersDark = window.matchMedia("(prefers-color-scheme: dark)").matches;
			return savedTheme || (prefersDark ? "dark" : "light");
		}
		return "light"; // Default for SSR
	});

	const [colorTheme, setColorThemeState] = useState<string>(() => {
		if (typeof window !== "undefined") {
			return localStorage.getItem("colorTheme") || "gaming";
		}
		return "gaming"; // Default for SSR
	});

	// Function to load CSS theme
	const loadThemeCSS = (themeName: string) => {
		// Remove existing theme CSS
		const existingThemeLinks = document.querySelectorAll("link[data-theme-css]");
		existingThemeLinks.forEach((link) => link.remove());

		// Add new theme CSS
		const themeConfig = availableThemes.find((t: ThemeConfig) => t.name === themeName);
		if (themeConfig) {
			const link = document.createElement("link");
			link.rel = "stylesheet";
			link.href = themeConfig.cssFile;
			link.setAttribute("data-theme-css", themeName);
			document.head.appendChild(link);
		}
	};

	const setColorTheme = (themeName: string) => {
		setColorThemeState(themeName);
		localStorage.setItem("colorTheme", themeName);
		loadThemeCSS(themeName);
	};

	useEffect(() => {
		localStorage.setItem("theme", theme);
		if (theme === "dark") {
			document.documentElement.classList.add("dark");
		} else {
			document.documentElement.classList.remove("dark");
		}
	}, [theme]);

	// Load color theme on mount and when it changes
	useEffect(() => {
		loadThemeCSS(colorTheme);
	}, [colorTheme]);

	const toggleTheme = () => {
		setTheme((prevTheme) => (prevTheme === "light" ? "dark" : "light"));
	};

	const colors = getThemeColors();

	return (
		<ThemeContext.Provider
			value={{
				theme,
				colorTheme,
				colors,
				toggleTheme,
				setColorTheme,
				availableColorThemes: availableThemes,
			}}
		>
			{children}
		</ThemeContext.Provider>
	);
};

export const useTheme = () => {
	const context = useContext(ThemeContext);
	if (context === undefined) {
		throw new Error("useTheme must be used within a ThemeProvider");
	}
	return context;
};

// Helper function to get the current theme
export const getCurrentTheme = (): Theme => {
	if (typeof window !== "undefined") {
		const savedTheme = localStorage.getItem("theme") as Theme | null;
		const prefersDark = window.matchMedia("(prefers-color-scheme: dark)").matches;
		return savedTheme || (prefersDark ? "dark" : "light");
	}
	return "light"; // Default to light theme during SSR
};
